import { DestroyRef, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subject, catchError, debounceTime, of, switchMap, take, tap } from 'rxjs';
import { GridRow, GridRowsPage, GridGroupSummaryItem } from '../contracts/public-api';
import { COLLAPSE_CARDINALITY_THRESHOLD, MAX_PARALLEL_GROUPS } from './grid-orchestration.constants';
import { GridOrchestrationLogLevel, logGridOrchestration } from './grid-orchestration.dev-log';
import type { GroupPaginationState } from './grid-orchestration.types';
import type {
  ElementsQueryPipelineHost,
  FetchGridPageFn,
  GridWhereClause
} from './grid-orchestration-query-pipeline.types';
import { FLAT_FETCH_THRESHOLD } from './grid-orchestration.utils';

export type {
  DiscoverSubGroupsViaTreeFn,
  ElementsQueryPipelineFetchDelegates,
  ElementsQueryPipelineHost,
  ElementsQueryPipelineStateDelegates,
  FetchAllPrimaryGroupSummariesFn,
  FetchGridPageFn,
  FetchPerGroupFirstPagesFn,
  FetchPerTypeFirstPagesFn,
  GridWhereClause,
  SeedPrimaryGroupSkeletonWithHybridEagerFn
} from './grid-orchestration-query-pipeline.types';

/**
 * Concatenates cursor pages from {@link FetchGridPageFn} until the connection is
 * exhausted or `maxRows` is reached.
 *
 * `fetchGridPage` is hard-capped at 100 rows. Client-side grouping under
 * {@link FLAT_FETCH_THRESHOLD} must walk remaining pages; otherwise the grid
 * stops at the first page (the zoomed-out map-scope bug).
 *
 * @param fetchGridPage - Page loader that already includes map-scope `where`.
 * @param where - Current grid filter.
 * @param maxRows - Stop after this many accumulated rows.
 * @returns Combined page, preserving the last `pageInfo` / `totalCount`.
 */
export function fetchAllGridPages(
  fetchGridPage: FetchGridPageFn,
  where: GridWhereClause,
  maxRows: number
): Observable<GridRowsPage> {
  const loadPage = (after: string | undefined, accumulated: Array<GridRow>): Observable<GridRowsPage> => {
    const page$: Observable<GridRowsPage> =
      after === undefined ? fetchGridPage(where) : fetchGridPage(where, after);
    return page$.pipe(
      switchMap((page: GridRowsPage): Observable<GridRowsPage> => {
        const nodes: Array<GridRow> = accumulated.concat(page.nodes);
        const endCursor: string | null = page.pageInfo?.endCursor ?? null;
        const hasNextPage: boolean = page.pageInfo?.hasNextPage === true;
        const reachedCap: boolean = nodes.length >= maxRows;
        if (!hasNextPage || endCursor === null || reachedCap) {
          const combined: GridRowsPage = {
            nodes,
            totalCount: page.totalCount,
            // The cap stops draining, not paging. Reporting the server's real
            // `hasNextPage` lets scroll-driven loadMore resume from `endCursor`.
            pageInfo: {
              endCursor,
              hasNextPage: hasNextPage && endCursor !== null
            }
          };
          return of(combined);
        }
        return loadPage(endCursor, nodes);
      })
    );
  };

  return loadPage(undefined, new Array<GridRow>());
}

/**
 * Map-scoped flat fetch used when grouping is applied client-side.
 * Clears per-bucket pagination so later `loadMore` continues this cursor.
 *
 * @param host - Query pipeline host.
 * @param where - Current grid filter.
 * @returns Concatenated rows up to {@link FLAT_FETCH_THRESHOLD}.
 */
function fetchFlatPagesForClientGrouping(
  host: ElementsQueryPipelineHost,
  where: GridWhereClause
): Observable<GridRowsPage> {
  host.setGroupPagination(null);
  return fetchAllGridPages(host.fetchGridPage, where, FLAT_FETCH_THRESHOLD);
}

/**
 * Subscribes to the debounced elements query pipeline.
 * Fetches the first page of results; subsequent pages load via pagination helpers.
 */
export function subscribeElementsQueryPipeline(
  host: ElementsQueryPipelineHost,
  elementsQuery$: Subject<GridWhereClause>,
  selectedElementTypes: Signal<Array<string>>,
  destroyRef: DestroyRef
): void {
  elementsQuery$
    .pipe(
      debounceTime(300),
      tap((where: GridWhereClause): void => {
        host.resetForNewQuery(where);
      }),
      switchMap((where: GridWhereClause): Observable<GridRowsPage> => {
        const groupFields: Array<string> = host.getGridGroupByFields();

        if (groupFields.length > 0) {
          const primaryGroup: string = groupFields[0];
          host.setPrimaryGroupField(primaryGroup);

          if (primaryGroup === host.getDiscriminatorField()) {
            const subGroupFields: Array<string> = groupFields.slice(1);
            if (subGroupFields.length > 0) {
              return fetchFlatPagesForClientGrouping(host, where).pipe(
                switchMap((response: GridRowsPage): Observable<GridRowsPage> =>
                  host.discoverSubGroupsViaTree(selectedElementTypes, groupFields, where, response)
                )
              );
            }
            return host.fetchPerTypeFirstPages(selectedElementTypes, where);
          }

          return host.fetchAllPrimaryGroupSummaries(primaryGroup, selectedElementTypes(), where).pipe(
            take(1),
            tap((): void => {
              host.setGridSummaryLoading(false);
            }),
            switchMap((groups: Array<GridGroupSummaryItem>): Observable<GridRowsPage> => {
              if (groups.length === 0) {
                host.setPrimaryGroupField(null);
                host.setGroupPagination(null);
                return host.fetchGridPage(where);
              }

              const summaryTotal: number = groups.reduce(
                (sum: number, group: GridGroupSummaryItem): number => sum + group.count,
                0
              );
              const subGroupFields: Array<string> = groupFields.slice(1);

              if (summaryTotal <= FLAT_FETCH_THRESHOLD) {
                const flatPages$: Observable<GridRowsPage> = fetchFlatPagesForClientGrouping(host, where);
                if (subGroupFields.length > 0) {
                  return flatPages$.pipe(
                    switchMap((response: GridRowsPage): Observable<GridRowsPage> =>
                      host.discoverSubGroupsViaTree(selectedElementTypes, groupFields, where, response)
                    )
                  );
                }
                return flatPages$;
              }

              if (groups.length > COLLAPSE_CARDINALITY_THRESHOLD) {
                host.addHighCardinalityField(primaryGroup);
                const skeleton$: Observable<GridRowsPage> = host.seedPrimaryGroupSkeletonWithHybridEager(
                  primaryGroup,
                  groups,
                  where
                );
                host.setGridLoading(false);
                if (subGroupFields.length > 0) {
                  return skeleton$.pipe(
                    switchMap((response: GridRowsPage): Observable<GridRowsPage> =>
                      host.discoverSubGroupsViaTree(selectedElementTypes, groupFields, where, response)
                    )
                  );
                }
                return skeleton$;
              }

              if (groups.length > MAX_PARALLEL_GROUPS) {
                host.reportGroupingCapExceeded({
                  kind: 'primaryGroups',
                  skippedCount: groups.length - MAX_PARALLEL_GROUPS,
                  cap: MAX_PARALLEL_GROUPS
                });
              }
              const capped: Array<GridGroupSummaryItem> = groups.slice(0, MAX_PARALLEL_GROUPS);
              const firstPages$: Observable<GridRowsPage> = host.fetchPerGroupFirstPages(
                primaryGroup,
                capped,
                where
              );
              if (subGroupFields.length > 0) {
                return firstPages$.pipe(
                  switchMap((response: GridRowsPage): Observable<GridRowsPage> =>
                    host.discoverSubGroupsViaTree(selectedElementTypes, groupFields, where, response)
                  )
                );
              }
              return firstPages$;
            }),
            catchError((err: Error): Observable<GridRowsPage> => {
              logGridOrchestration(
                GridOrchestrationLogLevel.Error,
                `Group summary failed for '${primaryGroup}', falling back to flat mode:`,
                err
              );
              host.setGridSummaryLoading(false);
              host.setPrimaryGroupField(null);
              host.setGroupPagination(null);
              return host.fetchGridPage(where);
            })
          );
        }

        host.setPrimaryGroupField(null);
        host.setGroupPagination(null);
        return host.fetchGridPage(where).pipe(
          catchError((err: Error): Observable<never> => {
            host.setElementsError(err.message);
            logGridOrchestration(GridOrchestrationLogLevel.Error, 'Failed to load elements:', err);
            return EMPTY;
          })
        );
      }),
      tap((response: GridRowsPage): void => {
        if (host.getGroupPagination() !== null) {
          host.mergeGroupPaginationIntoElements();
        } else {
          host.setElements(response);
        }
      }),
      tap((response: GridRowsPage): void => {
        const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
        if (pagination === null) {
          host.setGridEndCursor(response.pageInfo?.endCursor ?? null);
          host.setGridHasMore(response.pageInfo?.hasNextPage ?? false);
        } else {
          host.setGridHasMore(
            Array.from(pagination.values()).some((state: GroupPaginationState): boolean => state.hasNextPage)
          );
        }
      }),
      tap((): void => {
        host.setGridLoading(false);
        host.setGridSummaryLoading(false);
      }),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe();
}
